import React, { useEffect } from 'react';
import { Card, Typography, Row, Col, Button } from 'antd';
import { useHistory } from 'react-router-dom';
import { ShoppingCartOutlined, SunOutlined, HomeOutlined, ClockCircleOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const Miami = () => {
  const history = useHistory();

  useEffect(() => {
    const localSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Comfort Bears in Miami - Lumi Bears",
      "description": "Premium comfort companions for Miami families. Emotional support bears designed to help children in Miami with transitions and cultural diversity navigation.",
      "url": "https://lumibears.com/locations/miami", 
      "about": {
        "@type": "Product",
        "name": "Lumi Comfort Bears",
        "description": "Emotional support companions for children"
      },
      "audience": {
        "@type": "Audience",
        "geographicArea": {
          "@type": "City",
          "name": "Miami", 
          "containedInPlace": {
            "@type": "State",
            "name": "Florida"
          }
        }
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(localSchema);
    document.head.appendChild(script);

    document.title = "Comfort Bears Miami - Emotional Support for Miami Children | Lumi Bears";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Premium comfort bears for Miami families. Help Miami children with cultural diversity and transition support. Fast shipping to Coral Gables, Brickell, Aventura.');
    }

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent.includes('miami')) {
        existingScript.remove();
      }
      document.title = "Lumi Bears - Comfort Companions for Sensitive Children | Emotional Support Teddy Bears";
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Premium comfort companions for children who need extra emotional support. Soft, intelligent teddy bears designed to help kids feel calm and secure during bedtime and challenging moments.');
      }
    };
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }}>
      {/* Hero Section */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '60px 0',
        color: 'white'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <Row gutter={[48, 32]} align="middle">
            <Col xs={24} lg={14}>
              <Title level={1} style={{ color: 'white', marginBottom: '20px', fontSize: '2.5rem' }}>
                Comfort Bears in Miami
              </Title>
              <Paragraph style={{ fontSize: '18px', color: 'rgba(255,255,255,0.9)', marginBottom: '30px' }}>
                Supporting Miami families with premium comfort companions designed for children who need 
                extra emotional support. From South Beach to Coral Gables, Wynwood to Key Biscayne - 
                helping your child thrive in the Magic City.
              </Paragraph>
              <Button 
                type="primary" 
                size="large" 
                icon={<ShoppingCartOutlined />}
                onClick={() => history.push('/')}
                style={{ 
                  background: 'white', 
                  borderColor: 'white', 
                  color: '#667eea',
                  height: '50px',
                  fontSize: '16px'
                }}
              >
                Shop Comfort Bears
              </Button>
            </Col>
            <Col xs={24} lg={10}>
              <div style={{ textAlign: 'center' }}>
                <SunOutlined style={{ fontSize: '120px', color: 'rgba(255,255,255,0.3)' }} />
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '60px 20px' }}>
        
        {/* Why Miami Families Choose Lumi Bears */}
        <Row gutter={[48, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} lg={16}>
            <Title level={2} style={{ color: '#333', marginBottom: '24px' }}>
              Why Miami Families Trust Lumi Bears
            </Title>
            <Paragraph style={{ fontSize: '16px', lineHeight: '1.7', color: '#666', marginBottom: '20px' }}>
              Miami's unique blend of cultures, languages, and rapid growth creates both incredible opportunities 
              and complex challenges for families. From navigating multiple languages at home and school to 
              adapting to Miami's fast-paced lifestyle, children need emotional support tools that help them 
              stay grounded while embracing all the city has to offer.
            </Paragraph>
            <Paragraph style={{ fontSize: '16px', lineHeight: '1.7', color: '#666' }}>
              Whether you're in downtown condos, suburban Kendall, or beachside communities, our [comfort bears](/products) 
              provide consistent emotional support that helps Miami children build confidence and resilience in 
              this vibrant, multicultural environment.
            </Paragraph>
          </Col>
          <Col xs={24} lg={8}>
            <Card style={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <HomeOutlined style={{ fontSize: '48px', color: '#667eea', marginBottom: '16px' }} />
              <Title level={4} style={{ color: '#333' }}>Multicultural Ready</Title>
              <Text style={{ color: '#666' }}>
                Designed to support children navigating Miami's rich cultural diversity and bilingual environments.
              </Text>
            </Card>
          </Col>
        </Row>

        {/* Shipping & Delivery Info */}
        <Card style={{ marginBottom: '40px', background: '#fef7f0' }}>
          <Row gutter={[32, 24]} align="middle">
            <Col xs={24} md={4} style={{ textAlign: 'center' }}>
              <ClockCircleOutlined style={{ fontSize: '48px', color: '#667eea' }} />
            </Col>
            <Col xs={24} md={20}>
              <Title level={3} style={{ color: '#333', marginBottom: '16px' }}>
                Fast Delivery to Greater Miami Area
              </Title>
              <Row gutter={[24, 16]}>
                <Col xs={24} sm={8}>
                  <Text strong style={{ color: '#333' }}>Miami Beach & Downtown:</Text>
                  <br />
                  <Text style={{ color: '#666' }}>2-3 weeks delivery</Text>
                </Col>
                <Col xs={24} sm={8}>
                  <Text strong style={{ color: '#333' }}>Coral Gables & Coconut Grove:</Text>
                  <br />
                  <Text style={{ color: '#666' }}>2-3 weeks delivery</Text>
                </Col>
                <Col xs={24} sm={8}>
                  <Text strong style={{ color: '#333' }}>Kendall & Aventura:</Text>
                  <br />
                  <Text style={{ color: '#666' }}>2-3 weeks delivery</Text>
                </Col>
              </Row>
              <Paragraph style={{ marginTop: '16px', color: '#666', marginBottom: 0 }}>
                <Text italic>
                  All orders ship with tracking. Current delivery time reflects our pre-order production schedule 
                  as we carefully craft each comfort companion for Florida families.
                </Text>
              </Paragraph>
            </Col>
          </Row>
        </Card>

        {/* Miami-Specific Challenges */}
        <Title level={2} style={{ color: '#333', marginBottom: '30px', textAlign: 'center' }}>
          Supporting Miami Children Through Cultural Richness
        </Title>
        
        <Row gutter={[32, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} md={8}>
            <Card style={{ height: '100%', textAlign: 'center' }}>
              <Title level={4} style={{ color: '#333' }}>Multilingual Navigation</Title>
              <Paragraph style={{ color: '#666' }}>
                Many Miami children navigate multiple languages daily - Spanish, English, and others at home, school, 
                and in their communities. [Comfort bears](/products/lumi-gentle) provide consistent emotional support during 
                language transitions and cultural code-switching challenges.
              </Paragraph>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ height: '100%', textAlign: 'center' }}>
              <Title level={4} style={{ color: '#333' }}>Cultural Identity Balance</Title>
              <Paragraph style={{ color: '#666' }}>
                Balancing family heritage with American culture while developing personal identity can be complex for children. 
                Comfort bears offer emotional regulation support during cultural identity exploration and family expectations.
              </Paragraph>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ height: '100%', textAlign: 'center' }}>
              <Title level={4} style={{ color: '#333' }}>Rapid City Changes</Title>
              <Paragraph style={{ color: '#666' }}>
                Miami's constant development and growth means neighborhoods, schools, and social environments change frequently. 
                Comfort bears provide stability and emotional support during transitions and community changes.
              </Paragraph>
            </Card>
          </Col>
        </Row>

        {/* Family Resources for Miami */}
        <Card style={{ marginBottom: '40px' }}>
          <Title level={3} style={{ color: '#333', marginBottom: '24px' }}>
            Resources for Miami Families
          </Title>
          <Row gutter={[32, 24]}>
            <Col xs={24} md={12}>
              <Title level={5} style={{ color: '#667eea' }}>Child Development Support</Title>
              <ul style={{ color: '#666', lineHeight: '1.6' }}>
                <li>
                  <a href="https://www.nicklauschildrens.org/conditions/behavioral-health" 
                     target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>
                    Nicklaus Children's Hospital - Behavioral Health
                  </a>
                </li>
                <li>
                  <a href="https://www.dadeschools.net/students/studentservices/psychservices" 
                     target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>
                    Miami-Dade Schools - Psychological Services
                  </a>
                </li>
              </ul>
            </Col>
            <Col xs={24} md={12}>
              <Title level={5} style={{ color: '#667eea' }}>Family Activities & Wellness</Title>
              <ul style={{ color: '#666', lineHeight: '1.6' }}>
                <li>
                  <a href="https://www.miamidade.gov/parks/programs.asp" 
                     target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>
                    Miami-Dade Parks - Family Programs
                  </a>
                </li>
                <li>
                  <a href="https://www.mdpls.org/kids-teens" 
                     target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>
                    Miami-Dade Public Library - Children's Programs
                  </a>
                </li>
              </ul>
            </Col>
          </Row>
        </Card>

        {/* Areas We Serve */}
        <Card style={{ marginBottom: '40px', background: '#f5f5f5' }}>
          <Title level={3} style={{ color: '#333', marginBottom: '24px' }}>
            Serving Miami Areas & Communities
          </Title>
          <Row gutter={[24, 16]}>
            <Col xs={12} md={6}>
              <Text strong style={{ color: '#667eea' }}>Miami Beach:</Text>
              <ul style={{ color: '#666', margin: '8px 0', paddingLeft: '16px' }}>
                <li>South Beach</li>
                <li>Mid Beach</li>
                <li>North Beach</li>
                <li>Fisher Island</li>
              </ul>
            </Col>
            <Col xs={12} md={6}>
              <Text strong style={{ color: '#667eea' }}>Central Miami:</Text>
              <ul style={{ color: '#666', margin: '8px 0', paddingLeft: '16px' }}>
                <li>Downtown</li>
                <li>Brickell</li>
                <li>Wynwood</li>
                <li>Little Havana</li>
              </ul>
            </Col>
            <Col xs={12} md={6}>
              <Text strong style={{ color: '#667eea' }}>South Miami:</Text>
              <ul style={{ color: '#666', margin: '8px 0', paddingLeft: '16px' }}>
                <li>Coral Gables</li>
                <li>Coconut Grove</li>
                <li>Kendall</li>
                <li>Pinecrest</li>
              </ul>
            </Col>
            <Col xs={12} md={6}>
              <Text strong style={{ color: '#667eea' }}>North Miami:</Text>
              <ul style={{ color: '#666', margin: '8px 0', paddingLeft: '16px' }}>
                <li>Aventura</li>
                <li>Bal Harbour</li>
                <li>Sunny Isles</li>
                <li>Key Biscayne</li>
              </ul>
            </Col>
          </Row>
        </Card>

        {/* Call to Action */}
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
          borderRadius: '12px'
        }}>
          <Title level={2} style={{ color: '#4a148c', marginBottom: '20px' }}>
            Ready to Support Your Miami Child?
          </Title>
          <Paragraph style={{ fontSize: '18px', color: '#6a1b9a', marginBottom: '30px', maxWidth: '600px', margin: '0 auto 30px auto' }}>
            Join Miami families who are giving their children the emotional support tools they need to thrive 
            in this diverse, dynamic international city.
          </Paragraph>
          <Button 
            type="primary" 
            size="large" 
            icon={<ShoppingCartOutlined />}
            onClick={() => history.push('/')}
            style={{ 
              background: '#4a148c', 
              borderColor: '#4a148c',
              height: '50px',
              fontSize: '18px',
              padding: '0 30px'
            }}
          >
            Shop Comfort Bears
          </Button>
        </div>

      </div>
    </div>
  );
};

export default Miami;